/**
 * MiniMax Hailuo 2.3 官方视频接口
 *
 * POST /v1/video_generation
 * GET  /v1/query/video_generation?task_id=...
 * GET  /v1/files/retrieve?file_id=...
 *
 * Success 后使用 retrieve 获取 download_url（有效期 1 小时），
 * 前端下载为 File 后上传到自己服务器获取持久 URL。
 */
import axios from 'axios'
import { extractApiErrorMessage } from '../../extract-api-error-message'
import { saveMediaBlob } from '@/utils/mediaCache'

var POLL_INTERVAL = 3000
var POLL_TIMEOUT = 10 * 60 * 1000

var POST_PATH = '/v1/video_generation'
var QUERY_PATH = '/v1/query/video_generation'
var RETRIEVE_PATH = '/v1/files/retrieve'

function getMiniMaxConfig() {
  return {
    baseURL: (process.env.VUE_APP_OFFICIAL_MINIMAX_HAILUO_BASE_URL || 'https://api.minimaxi.com').replace(/\/$/, ''),
    apiKey: (process.env.VUE_APP_OFFICIAL_MINIMAX_HAILUO_API_KEY || '').trim()
  }
}

function createClient() {
  var cfg = getMiniMaxConfig()
  if (!cfg.baseURL) throw new Error('未配置 VUE_APP_OFFICIAL_MINIMAX_HAILUO_BASE_URL')
  if (!cfg.apiKey) throw new Error('未配置 VUE_APP_OFFICIAL_MINIMAX_HAILUO_API_KEY')
  var instance = axios.create({
    baseURL: cfg.baseURL,
    timeout: 120000,
    headers: {
      Authorization: 'Bearer ' + cfg.apiKey,
      'Content-Type': 'application/json'
    }
  })
  instance.interceptors.response.use(
    function (res) { return res.data },
    function (err) {
      var respData = err.response && err.response.data
      var msg =
        extractApiErrorMessage(respData) ||
        (respData && (respData.message || respData.messge)) ||
        (respData && respData.base_resp && respData.base_resp.status_msg) ||
        err.message ||
        '请求失败'
      return Promise.reject(new Error(msg))
    }
  )
  return instance
}

function normalizeDuration(duration) {
  var d = parseInt(duration, 10)
  if (d !== 6 && d !== 10) d = 6
  return d
}

function normalizeResolution(resolution, duration) {
  var r = String(resolution || '').toUpperCase()
  if (r !== '768P' && r !== '1080P') r = '768P'
  if (duration === 10 && r === '1080P') r = '768P'
  return r
}

function ensureBaseRespOk(data, fallbackMsg) {
  var baseResp = data && data.base_resp
  var statusCode = baseResp && baseResp.status_code
  if (statusCode == null || Number(statusCode) === 0) return
  var msg = (baseResp && baseResp.status_msg) || fallbackMsg || '请求失败'
  throw new Error(msg)
}

function parseQueryStatus(data) {
  ensureBaseRespOk(data, '查询任务状态失败')
  var statusRaw = data && data.status ? String(data.status) : ''
  var status = statusRaw.toLowerCase()
  var fileId = data && data.file_id ? String(data.file_id) : ''
  var statusMsg = (data && data.base_resp && data.base_resp.status_msg) || (data && data.status_msg) || ''

  if (status === 'success') {
    if (!fileId) {
      return { status: 'failed', error: '任务成功但未返回 file_id', fileId: null }
    }
    return { status: 'completed', error: null, fileId: fileId }
  }
  if (status === 'fail') {
    return { status: 'failed', error: statusMsg || '生成失败', fileId: null }
  }
  if (status === 'preparing' || status === 'queueing' || status === 'processing') {
    return { status: 'processing', error: null, fileId: null }
  }
  return { status: 'failed', error: '任务状态未知：' + statusRaw, fileId: null }
}

function retrieveDownloadMeta(client, fileId) {
  return client.get(RETRIEVE_PATH, {
    params: { file_id: fileId }
  })
}

var HAILUO_VIDEO_OSS_HOST = 'public-cdn-video-data-algeng.oss-cn-wulanchabu.aliyuncs.com'

function proxyHailuoVideoUrl(url) {
  try {
    var u = new URL(url)
    if (u.host === HAILUO_VIDEO_OSS_HOST) {
      return '/ossHailuoVideoProxy' + u.pathname + u.search
    }
  } catch (e) {}
  return url
}

function downloadVideoBlob(downloadUrl) {
  var fetchUrl = proxyHailuoVideoUrl(downloadUrl)
  return fetch(fetchUrl)
    .then(function (res) {
      if (!res.ok) return Promise.reject(new Error('download video failed: HTTP ' + res.status))
      return res.blob()
    })
    .then(function (blob) {
      if (!blob || !blob.size) return Promise.reject(new Error('download video failed: empty blob'))
      return blob
    })
}

export function submitMiniMaxHailuo23Task(params) {
  var client = createClient()
  var prompt = (params.prompt || '').trim().slice(0, 2000)
  if (!prompt) return Promise.reject(new Error('prompt 必填'))
  var videoMode = params.videoMode || 'text'
  var duration = normalizeDuration(params.videoDuration)
  var resolution = normalizeResolution(params.videoRes, duration)

  var body = {
    model: 'MiniMax-Hailuo-2.3',
    prompt: prompt,
    duration: duration,
    resolution: resolution
  }
  if (videoMode === 'image') {
    if (!params.referenceImage) return Promise.reject(new Error('first_frame_image 必填（图生视频）'))
    body.first_frame_image = String(params.referenceImage)
    body.prompt_optimizer = !!params.promptEnhance
  }

  return client.post(POST_PATH, body).then(function (data) {
    ensureBaseRespOk(data, '提交任务失败')
    var taskId = data && data.task_id ? String(data.task_id) : ''
    if (!taskId) return Promise.reject(new Error('官方接口未返回 task_id'))
    return { taskId: taskId }
  })
}

export function pollMiniMaxHailuo23Task(taskId, callback, interval) {
  var pollInterval = interval || POLL_INTERVAL
  var deadline = Date.now() + POLL_TIMEOUT
  var pollTimer = null
  var stopped = false
  var resolvingSuccess = false
  var client = createClient()

  function stop() {
    stopped = true
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  function poll() {
    if (stopped) return
    client
      .get(QUERY_PATH, { params: { task_id: taskId } })
      .then(function (data) {
        if (stopped) return
        var parsed = parseQueryStatus(data)

        if (parsed.status === 'completed') {
          if (resolvingSuccess) return
          resolvingSuccess = true
          retrieveDownloadMeta(client, parsed.fileId)
            .then(function (retrieveData) {
              ensureBaseRespOk(retrieveData, '获取文件信息失败')
              var file = retrieveData && retrieveData.file ? retrieveData.file : null
              var downloadUrl = file && file.download_url ? String(file.download_url) : ''
              if (!downloadUrl) return Promise.reject(new Error('retrieve 未返回 download_url'))

              return downloadVideoBlob(downloadUrl).then(function (blob) {
                parsed.videoBlob = blob
                parsed.url = downloadUrl
                parsed.taskId = taskId
                if (taskId) {
                  return saveMediaBlob(taskId, blob).then(function () {
                    return parsed
                  })
                }
                return parsed
              })
            })
            .then(function (result) {
              if (typeof callback === 'function') {
                var finalResult = {
                  status: 'completed',
                  url: result.url || '',
                  videoBlob: result.videoBlob || null,
                  error: null,
                  historyId: result.historyId,
                  taskId: result.taskId || taskId
                }
                callback(null, finalResult)
              }
              stop()
            })
            .catch(function (err) {
              if (typeof callback === 'function') callback(err, null)
              stop()
            })
          return
        }

        if (parsed.status === 'failed') {
          if (typeof callback === 'function') {
            callback(null, { status: 'failed', url: null, error: parsed.error || '生成失败' })
          }
          stop()
          return
        }

        if (typeof callback === 'function') {
          callback(null, { status: 'processing', url: null, error: null })
        }
        if (Date.now() > deadline) {
          if (typeof callback === 'function') callback(new Error('轮询超时'), null)
          stop()
        } else {
          pollTimer = setTimeout(poll, pollInterval)
        }
      })
      .catch(function (err) {
        if (stopped) return
        if (typeof callback === 'function') callback(err, null)
        stop()
      })
  }

  poll()
  return { stop: stop }
}
